import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useUserRegion } from '@frontend/components/agromarket/components/RegionSelector'
import { FundingProduct } from '@frontend/components/agromarket/core/access-control'
import { Modal } from '@frontend/ui-kit/agromarket/components/Modal'
import { useGetApprovedRegions } from '@/api/kubik/credit'
import { addModalToWellKnown } from '@/components/composed/Modal/constants'
import {
  isFieldEvaluationReportDialogAlreadyShown,
  markFieldEvaluationReportDialogAsAlreadyShown,
} from '@/pages/Financing/components/FieldEvaluationReportDialogLauncher/helpers'
import type { TFieldEvaluationReportDialogLauncherProps } from '@/pages/Financing/components/FieldEvaluationReportDialogLauncher/types'
import { FieldEvaluationReportDialog } from './components/FieldEvaluationReportDialog/FieldEvaluationReportDialog'
import { DEFAULT_OPEN_DIALOG_DELAY_MS } from './constants'

addModalToWellKnown({ FieldEvaluationReportDialog })

export const FieldEvaluationReportDialogLauncher = ({
  comment,
  timeout = DEFAULT_OPEN_DIALOG_DELAY_MS,
}: TFieldEvaluationReportDialogLauncherProps) => {
  const dispatch = useDispatch()
  const { userRegion } = useUserRegion()
  const [isAlreadyShown] = useState(() => isFieldEvaluationReportDialogAlreadyShown())

  const userRegionCode = userRegion?.code
  const hasOpenedRef = useRef(false)

  const { data: allowedRegions, isSuccess } = useGetApprovedRegions({
    productId: FundingProduct.REPORT_CTRL2GO,
  })

  const isAllowed = useMemo(
    () => Boolean(userRegionCode) && Boolean(allowedRegions?.approvedRegions?.includes(userRegionCode)),
    [allowedRegions, userRegionCode],
  )

  useEffect(() => {
    if (isAlreadyShown || !isSuccess || !isAllowed || hasOpenedRef.current) {
      return
    }

    const timer = setTimeout(() => {
      if (hasOpenedRef.current) return

      hasOpenedRef.current = true

      markFieldEvaluationReportDialogAsAlreadyShown()

      Modal.show(FieldEvaluationReportDialog, {
        comment,
      })
    }, timeout)

    return () => clearTimeout(timer)
  }, [comment, dispatch, isAllowed, isAlreadyShown, isSuccess, timeout])

  return null
}
